// =============================================================================
// AppBuild Wrapper Bridge v2 — Protocol Constants & Types
// =============================================================================
// Single source of truth for the wire format. Every literal string the bridge
// puts on the wire — namespace, transport, platform, method, event type,
// error code, plugin name — has a constant here. Customers and host code
// should import these constants by name (PLATFORM_IOS, PUSH_GET_TOKEN, …)
// rather than typing string literals; that keeps usages searchable across
// both repos and prevents typos drifting between sender and receiver.
//
// Envelope shapes (see §2 of README.md):
//   Request  (page → host):   { ns, method, payload?, id? }
//   AppInfo  (host → page):   { ns, kind: 'appInfo',  payload }
//   Event    (host → page):   { ns, kind: 'event',    type, payload? }
//   Response (host → page):   { ns, kind: 'response', inReplyTo?, ok, payload?, error? }
//
// `ns` is the only required discriminator. Requests are identified by
// `method`; host→page messages by `kind`. There is no wire-level version
// field — the host advertises BRIDGE_V2_HOST_VERSION in
// appInfo.payload.version (informational only). Receivers are tolerant of
// extra fields (additive-tolerance keeps the protocol forward-compatible
// without versioning).
// -----------------------------------------------------------------------------

// ----- Envelope identity ----------------------------------------------------

export const BRIDGE_V2_NS = 'appbuild.bridge' as const;

/** Major version of the bridge this build implements. Advertised by the host
 *  in `appInfo.payload.version` (informational only — there is no wire
 *  version field and no negotiation). If a breaking change is ever needed,
 *  a new bridge generation lives in a new module (e.g. `bridge-v3`); this
 *  major is expected to stay on 2 long-term. */
export const BRIDGE_V2_HOST_VERSION = 2 as const;

/** Origin marker stamped on host-injected MessageEvents inside the InAppBrowser
 *  WebView. Used by the outbound forwarder to skip messages we ourselves
 *  delivered (loop guard). */
export const BRIDGE_V2_NATIVE_ORIGIN = 'appbuild://native' as const;

// ----- Platform -------------------------------------------------------------

export const PLATFORM_IOS = 'ios' as const;
export const PLATFORM_ANDROID = 'android' as const;
export const PLATFORM_WEB = 'web' as const;

export type BridgeV2Platform =
  | typeof PLATFORM_IOS
  | typeof PLATFORM_ANDROID
  | typeof PLATFORM_WEB;

// ----- Transport ------------------------------------------------------------

export const TRANSPORT_NATIVE = 'native' as const;
export const TRANSPORT_PWA = 'pwa' as const;

export type BridgeV2Transport = typeof TRANSPORT_NATIVE | typeof TRANSPORT_PWA;

// ----- Message kinds (host → page only) ------------------------------------
// Requests carry no `kind` — they are identified by the presence of `method`.

/** Snapshot of app/runtime info — appId, platform, capabilities, push token.
 *  Broadcast once on page load, and re-emitted on demand (Core.getAppInfo) or
 *  whenever the host detects a change (e.g. permission grant). */
export const KIND_APP_INFO = 'appInfo' as const;
export const KIND_EVENT = 'event' as const;
export const KIND_RESPONSE = 'response' as const;

export type BridgeV2Kind =
  | typeof KIND_APP_INFO
  | typeof KIND_EVENT
  | typeof KIND_RESPONSE;

// ----- Methods (request.method) --------------------------------------------

/** Re-fetch the appInfo payload — for customers who bound their listener too
 *  late to receive the page-load broadcast, or who want to refresh after a
 *  permission/capability change. The host responds with the same payload
 *  shape that the page-load broadcast uses. */
export const CORE_GET_APP_INFO = 'Core.getAppInfo' as const;

// Push first-class methods. There is deliberately no Push.register — push
// registration is owned by the host app (shell), not driven through this
// interface.
export const PUSH_CHECK_PERMISSIONS = 'Push.checkPermissions' as const;
export const PUSH_REQUEST_PERMISSIONS = 'Push.requestPermissions' as const;
/** Primary identifier for the device's push registration — the
 *  `push_tokens.id` UUID. Use this for targeting and enrichment. */
export const PUSH_GET_ID = 'Push.getId' as const;
/** Debug-only: returns the raw OS push token (native) or the same id (PWA).
 *  Most wrappers should use `Push.getId` instead. */
export const PUSH_GET_TOKEN = 'Push.getToken' as const;
/** Attach a wrapper-owned customer identifier (e.g. ecommerce customer id)
 *  to this push registration. Stored locally and flushed when registered. */
export const PUSH_SET_CUSTOMER_ID = 'Push.setCustomerId' as const;
/** Attach a wrapper-owned customer email to this push registration. */
export const PUSH_SET_CUSTOMER_EMAIL = 'Push.setCustomerEmail' as const;
/** Read current enrichment state for this app. */
export const PUSH_GET_CUSTOMER = 'Push.getCustomer' as const;
/** Clear all enrichment fields (id + email) — convenience for sign-out. */
export const PUSH_CLEAR_CUSTOMER = 'Push.clearCustomer' as const;
/** Round-trip test: the host emits a canned `push.received` event back to the
 *  caller so customers can verify their push-event wiring without a real push. */
export const PUSH_SIMULATE_PUSH_RECEIVED = 'Push.simulatePushReceived' as const;

// Camera first-class methods. The bridge accepts string `source`/`resultType`
// values so customer code stays plugin-agnostic; the host handler maps to
// `@capacitor/camera`'s TS enums internally.
export const CAMERA_CHECK_PERMISSIONS = 'Camera.checkPermissions' as const;
export const CAMERA_REQUEST_PERMISSIONS = 'Camera.requestPermissions' as const;
export const CAMERA_GET_PHOTO = 'Camera.getPhoto' as const;

// Plugin first-class methods. Generic across all second-class plugins.
//
// `Plugin.removeListener` is the unsubscribe side of the listener-handle
// round-trip. When a second-class plugin's addListener-style method returns
// a Capacitor `PluginListenerHandle` (`{ remove: () => Promise<void> }`),
// the host stashes the real handle in a per-page-load map and returns
// `{ [BRIDGE_LISTENER_HANDLE_MARKER]: '<handleId>' }` on the wire. The SDK
// wraps that marker as `{ remove: () => bridge.request('Plugin.removeListener',
// { id: handleId }) }` so customer code sees the standard Capacitor handle
// shape. Calling that .remove() round-trips back here, looks up the real
// handle, and calls its native .remove().
export const PLUGIN_REMOVE_LISTENER = 'Plugin.removeListener' as const;

// Second-class plugin passthrough — method shape is `plugin:<Name>.<method>`.
// The dispatcher routes any request whose `method` starts with this prefix to
// the generic passthrough handler, which forwards to
// `Capacitor.Plugins[Name][method](...args)` after reconstructing any callback
// markers in the payload.
//
// Wire shape for a passthrough request:
//   { method: 'plugin:<Name>.<method>', payload: { args: unknown[] }, id? }
//
// Within `args` (at any depth — arrays and plain object properties are walked
// recursively), function callbacks are serialised as
// `{ [BRIDGE_CALLBACK_MARKER]: '<cbId>' }`. On invocation the host
// reconstructs them as functions that emit a bridge event of type
// `plugin:callback:<cbId>` with the call arguments as the payload. Customers
// can subscribe to those events via the standard `bridge.on(type, cb)`
// pattern (their SDK does the marker bookkeeping; raw consumers post + listen
// for the event type directly).
export const PLUGIN_METHOD_PREFIX = 'plugin:' as const;
export const BRIDGE_CALLBACK_MARKER = '__bridgeCallback' as const;
/** Marker key for a host-side `PluginListenerHandle` reference on the wire.
 *  See PLUGIN_REMOVE_LISTENER for the round-trip flow. */
export const BRIDGE_LISTENER_HANDLE_MARKER = '__bridgeListenerHandle' as const;
/** Bridge event type used to deliver invocations of reconstructed plugin
 *  callbacks back to the wrapped page. `cbId` is the id the page (SDK)
 *  generated when it marked the callback in the outbound request. */
export const pluginCallbackEventType = (cbId: string): string =>
  `plugin:callback:${cbId}`;

export interface CameraGetPhotoOpts {
  quality?: number;
  allowEditing?: boolean;
  source?: 'camera' | 'photos' | 'prompt';
  /** `'dataUrl'` (default): self-contained `data:image/<format>;base64,…`,
   *  drop-in for `<img src>` and CSS `background-image`. `'uri'`: a blob URL
   *  / file path reference (no data inlined). */
  resultType?: 'dataUrl' | 'uri';
}

export interface CameraGetPhotoResult {
  format: 'dataUrl' | 'uri';
  /** Data URI (`data:image/…;base64,…`) when `format === 'dataUrl'`, or a
   *  blob URL / file path when `format === 'uri'`. */
  data: string;
  width?: number;
  height?: number;
}

export interface CameraPermissionsResult {
  camera: PermissionState;
  photos: PermissionState;
}

// ----- Event types (event.type) --------------------------------------------

export const EVENT_PUSH_RECEIVED = 'push.received' as const;

/** Payload shape carried by `push.received` events. Native forwards from
 *  `Capacitor.PushNotifications.pushNotificationReceived`; PWA forwards from
 *  the service worker via `usePWAPushMessageListener`. Both runtimes produce
 *  this exact shape. */
export interface PushReceivedPayload {
  title: string;
  body: string;
  data?: unknown;
}

// ----- Error codes (response.error.code) -----------------------------------

export const ERROR_NOT_IMPLEMENTED = 'NOT_IMPLEMENTED' as const;
export const ERROR_INTERNAL = 'INTERNAL_ERROR' as const;
export const ERROR_INVALID_REQUEST = 'INVALID_REQUEST' as const;
/** The user cancelled the action mid-flight (e.g. closed the camera/photo
 *  picker without picking). Not a failure — expected, just non-completing. */
export const ERROR_USER_CANCELLED = 'USER_CANCELLED' as const;
/** Handler did not resolve within BRIDGE_HANDLER_TIMEOUT_MS. The customer
 *  can retry; the request id is final (no late delivery once timeout fires). */
export const ERROR_TIMEOUT = 'TIMEOUT' as const;
/** Second-class passthrough target doesn't exist in this environment — the
 *  plugin isn't on the whitelist, isn't registered with Capacitor, or doesn't
 *  expose the requested method. Distinct from NOT_IMPLEMENTED (which is for
 *  first-class methods with no handler wired in this context). */
export const ERROR_PLUGIN_NOT_AVAILABLE = 'PLUGIN_NOT_AVAILABLE' as const;

// ----- Dispatcher limits ---------------------------------------------------

/** Maximum time a single handler may take before the dispatcher returns a
 *  TIMEOUT response. Prevents wedged plugins (Capacitor calls that never
 *  resolve, etc.) from hanging the request indefinitely. The customer SDK
 *  may enforce its own shorter timeout on top as a safety net. */
export const BRIDGE_HANDLER_TIMEOUT_MS = 30_000;

// ----- Permission states ---------------------------------------------------
// (These mirror Capacitor's PermissionState — kept as constants here so
// customer code can compare against named values rather than string literals.)

export const PERMISSION_GRANTED = 'granted' as const;
export const PERMISSION_DENIED = 'denied' as const;
export const PERMISSION_PROMPT = 'prompt' as const;

export type PermissionState =
  | typeof PERMISSION_GRANTED
  | typeof PERMISSION_DENIED
  | typeof PERMISSION_PROMPT;

// ----- Capacitor plugin names ----------------------------------------------
// First-class plugins (the host wraps these with structured helpers).

export const PLUGIN_PUSH_NOTIFICATIONS = 'PushNotifications' as const;
export const PLUGIN_CAMERA = 'Camera' as const;

// =============================================================================
// Envelope shapes
// =============================================================================

export interface AppInfoPayload {
  /** Major version of the bridge the host implements. Informational — there
   *  is no wire version field. See BRIDGE_V2_HOST_VERSION. */
  version: typeof BRIDGE_V2_HOST_VERSION;
  appId: string;
  /** The wrapper platform; push uses the same value (web push when transport === 'pwa', native APNs/FCM otherwise). */
  platform: BridgeV2Platform;
  transport: BridgeV2Transport;
  capabilities: Capabilities;
  push?: {
    /** Database UUID for this device's push registration (push_tokens.id).
     *  Null/undefined until the host has completed registration. Use this
     *  for targeting (external-push-notification targetDeviceIds) and for
     *  enrichment via Push.setCustomerId / Push.setCustomerEmail. */
    id?: string;
  };
}

export interface Capabilities {
  /** First-class: push has structured helpers (token, permission state) wired by the host. */
  push: {
    available: boolean;
    permission?: PermissionState;
  };
  /** First-class: camera has structured helpers wired by the host. */
  camera: { available: boolean };
  /**
   * Second-class plugins. Anything the host detects via Capacitor.isPluginAvailable
   * but does not wrap with first-class helpers (e.g. Health) lives here. Customers
   * drive these via a `plugin:<Name>.method` request method.
   */
  plugins: Record<string, { available: true; methods?: string[] }>;
}

/** Page → host. Identified by the presence of `method`. */
export interface BridgeV2Request<T = unknown> {
  ns: typeof BRIDGE_V2_NS;
  method: string;
  payload?: T;
  /** Optional correlation token; echoed to the response's `inReplyTo` if
   *  present. Provide one if you need to match a specific response. */
  id?: string;
}

// Host → page: a tagged union discriminated by `kind`.

export interface BridgeV2AppInfo {
  ns: typeof BRIDGE_V2_NS;
  kind: typeof KIND_APP_INFO;
  payload: AppInfoPayload;
}

export interface BridgeV2Event<T = unknown> {
  ns: typeof BRIDGE_V2_NS;
  kind: typeof KIND_EVENT;
  type: string;
  payload?: T;
}

export interface BridgeV2Response<T = unknown> {
  ns: typeof BRIDGE_V2_NS;
  kind: typeof KIND_RESPONSE;
  /** Echoes the requested method, so consumers can identify what a response
   *  is for without tracking the originating request (the SDK still uses
   *  `inReplyTo` to match Promises; `method` is for stateless tools — log
   *  panels, debug overlays, the bridge tester). */
  method?: string;
  /** Echo of the request's `id` if the request supplied one; undefined otherwise. */
  inReplyTo?: string;
  ok: boolean;
  payload?: T;
  error?: { code: string; message: string; data?: unknown };
}

export type BridgeV2Message =
  | BridgeV2Request
  | BridgeV2AppInfo
  | BridgeV2Event
  | BridgeV2Response;

// =============================================================================
// Helpers
// =============================================================================

/** A received envelope is a valid v2 message when its namespace matches.
 *  There is no wire version to validate; receivers tolerate extra fields
 *  (additive-tolerance) and discriminate requests vs host→page messages by
 *  the presence of `method` vs `kind`. */
export const isBridgeV2Message = (x: unknown): x is BridgeV2Message =>
  !!x &&
  typeof x === 'object' &&
  (x as { ns?: unknown }).ns === BRIDGE_V2_NS;

/** Generates a correlation id; senders that want to match a specific response
 *  should call this and put the result in their request's `id`. */
export const newBridgeV2Id = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const makeAppInfo = (payload: AppInfoPayload): BridgeV2AppInfo => ({
  ns: BRIDGE_V2_NS,
  kind: KIND_APP_INFO,
  payload,
});

export const makeEvent = <T>(type: string, payload?: T): BridgeV2Event<T> => ({
  ns: BRIDGE_V2_NS,
  kind: KIND_EVENT,
  type,
  payload,
});

export interface ResponseOk<T> { ok: true; payload?: T }
export interface ResponseErr { ok: false; error: { code: string; message: string; data?: unknown } }

/** Thrown by handlers when they need to produce a non-`INTERNAL_ERROR`
 *  response code (e.g. `USER_CANCELLED`). The dispatcher checks for this
 *  class and lifts `code` / `data` onto the response's `error` object;
 *  any other thrown value falls back to `INTERNAL_ERROR`. */
export class BridgeError extends Error {
  readonly code: string;
  readonly data?: unknown;
  constructor(code: string, message: string, data?: unknown) {
    super(message);
    this.name = 'BridgeError';
    this.code = code;
    this.data = data;
  }
}

export const makeResponse = <T>(
  request: Pick<BridgeV2Request, 'id' | 'method'>,
  result: ResponseOk<T> | ResponseErr,
): BridgeV2Response<T> => {
  const base = {
    ns: BRIDGE_V2_NS,
    kind: KIND_RESPONSE,
    method: request.method,
    inReplyTo: request.id,
  } as const;
  if (result.ok === true) {
    return { ...base, ok: true, payload: (result as ResponseOk<T>).payload };
  }
  return { ...base, ok: false, error: (result as ResponseErr).error };
};
